import { Check, CircleDashed, Minus, ShieldAlert, X } from "lucide-react";
import { stepSkipped, type PProcessStepState } from "@hisense/shared";

/**
 * Dấu trạng thái và thanh nấc dùng chung cho màn Quy trình P và màn Quy trình thường (29/10/2026) — hai màn đọc cùng một kiểu tiến độ
 * (`processProgress`), nên phải trông giống nhau.
 */
export function StateMark({ state }: { state: PProcessStepState["state"] }) {
  if (state === "done") return <Check className="size-3.5 text-ok" aria-hidden />;
  /**
   * TRƯỢT phải trông KHÁC HẲN, không phải một sắc thái của "chưa xong".
   *
   * Trước 15/09/2026 một lô trượt hiện dấu tích xanh như lô đạt, vì màn hình
   * chỉ hỏi "có bản ghi kiểm không". Người quản lý đọc xong yên tâm cho hàng
   * đi tiếp.
   */
  if (state === "failed") return <X className="size-3.5 text-danger" aria-hidden />;
  /**
   * CHO QUA CÓ PHÊ DUYỆT: đã trượt, nhưng quản trị cho hàng đi tiếp.
   *
   * Không dùng dấu tích xanh — lô này KHÔNG đạt, và dấu tích sẽ xoá mất việc
   * có người phải chịu trách nhiệm. Không dùng dấu X đỏ — chuỗi đã đi tiếp
   * thật, để đỏ là màn hình bảo đơn đang kẹt trong khi hàng đã giao. Dấu riêng,
   * màu cảnh báo.
   */
  if (state === "concession") return <ShieldAlert className="size-3.5 text-warn" aria-hidden />;
  if (state === "notApplicable") return <Minus className="size-3.5 text-ink-faint" aria-hidden />;
  // Tùy chọn mà không lập (29/10/2026): vòng nét đứt, màu nhạt — không phải chấm vàng "đang chờ", vì không ai phải làm gì.
  if (state === "optional") return <CircleDashed className="size-3.5 text-ink-faint" aria-hidden />;
  return <span className="block size-2 rounded-full bg-warn" aria-hidden />;
}

/** Thanh nấc: mỗi ô là một nấc của chuỗi, đọc được ngay từ xa. */
export function SeqBar({ seqs, steps }: { seqs: readonly number[]; steps: readonly PProcessStepState[] }) {
  return (
    <span className="ml-auto flex items-center gap-1" aria-hidden>
      {seqs.map((seq) => {
        const at = steps.filter((s) => s.seq === seq);
        const live = at.filter((s) => !stepSkipped(s.state));
        // Trượt thắng mọi màu khác: một nấc có bước trượt là nấc đỏ,
        // kể cả khi đường song song bên cạnh đã xong.
        const tone = !live.length
          ? "bg-line"
          : live.some((s) => s.state === "failed")
            ? "bg-danger"
            // Nấc đã qua nhờ phê duyệt: vàng, không xanh. Chuỗi
            // đi tiếp nhưng thanh tiến độ không được nói nó sạch.
            : live.some((s) => s.state === "concession")
              ? "bg-warn"
              : live.some((s) => s.state === "done")
                ? "bg-ok"
                : "bg-line-strong";
        return <span key={seq} className={`h-1.5 w-4 rounded-sm ${tone}`} />;
      })}
    </span>
  );
}
